<!--
  OrderDetailsStep: the booking panel after a vehicle is chosen.

  In order: the header (back to prices, vehicle and total, Edit stops, the
  route), the stops (each with an inline contact editor, one open at a
  time), What are you sending? (with one-tap suggestions), the Pickup time
  chip, then the optional extras as "add" chips (Loaders when the quote
  offers them, Preferred driver behind ORDER_FEATURES, Note with the
  manifest number). A sticky footer holds the hold note, the no-contact
  line, Confirm and the reason it is disabled.

  Dedicated (`dedicated`): the pickup time is required and Schedule only
  (2 hours to 30 days ahead), with a line saying Xobo assigns the driver.
  Everything else is as for On-Demand.

  NewRequest owns every value (v-model:*); this component lays them out,
  says what is missing, and emits the customer's intent. Nothing typed here
  changes the quote, so none of it needs a new price.
-->
<script lang="ts" setup>
import { computed, nextTick, ref, type ComponentPublicInstance } from "vue";
import { ArrowLeft, Close, Plus } from "@element-plus/icons-vue";
import ActionButton from "./ActionButton.vue";
import StopDetailsEditor from "./StopDetailsEditor.vue";
import LoadersField from "./LoadersField.vue";
import PreferredDriverField from "./PreferredDriverField.vue";
import PickupTimeField from "./PickupTimeField.vue";
import type { DraftLocation, StopDetails } from "../../utils/orderPayload";
import { GOODS_LIMITS, goodsDescriptionError, hasStopDetails } from "../../utils/orderPayload";
import { type PickupMode, scheduleError, SCHEDULE_MIN_LEAD_MINUTES, DEDICATED_MIN_LEAD_MINUTES } from "../../utils/orderTime";
import { formatPhone, shortPlaceName, stopLabel } from "../../utils/orderExtraInfo";

const props = defineProps<{
  vehicleName: string;
  total: number;
  locations: DraftLocation[];
  goods: string;
  pickupMode: PickupMode;
  pickupTime: string;
  notes: string;
  manifest: string;
  /** Clock from the parent, refreshed while the form is open. */
  now: number;
  placing: boolean;
  placed: boolean;
  /** A reason from a later-phase field (loaders, preferred driver). */
  extraBlocker?: string;
  /** A Dedicated booking (the Dedicated tab). */
  dedicated?: boolean;
  /** The quote's loaders offer, or null when it has none (no "+ Loaders"). */
  loadersOffer?: { unitFee: number; max: number } | null;
  /** Loaders booked (0 = none). */
  loadersCount?: number;
  /** ORDER_FEATURES.preferredDriver: whether "+ Preferred driver" shows. */
  preferredDriverEnabled?: boolean;
  /** The driver's phone or plate ("" = none). */
  preferredDriver?: string;
  /** The chosen quote's vehicle type, for the preferred-driver lookup. */
  vehicleTypeId?: number;
}>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "edit-stops"): void;
  /** The stop (by uid) and its cleaned details, from the inline editor. */
  (e: "save-stop", uid: string, details: StopDetails): void;
  (e: "confirm"): void;
  (e: "update:goods", v: string): void;
  (e: "update:pickupMode", v: PickupMode): void;
  (e: "update:pickupTime", v: string): void;
  (e: "update:notes", v: string): void;
  (e: "update:manifest", v: string): void;
  (e: "update:loadersCount", v: number): void;
  (e: "update:preferredDriver", v: string): void;
}>();

const kes = (n: number) => `KES ${Number(n || 0).toLocaleString()}`;

const routeSummary = computed(() => {
  const n = props.locations.length;
  if (n < 2) return "";
  const drops = n - 1;
  return `${shortPlaceName(props.locations[0].name)} → ${shortPlaceName(props.locations[n - 1].name)} · ${drops} drop${drops === 1 ? "" : "s"}`;
});

// ── What are you sending? ──
const goodsTouched = ref(false);
const goodsError = computed(() => goodsDescriptionError(props.goods));
const showGoodsError = computed(() => goodsTouched.value && !!goodsError.value);
// One tap fills the field; the customer can still type over it.
const GOODS_SUGGESTIONS = ["Documents", "Parcel", "Cartons", "Food", "Electronics"] as const;
function pickGoods(g: string) {
  emit("update:goods", g);
  goodsTouched.value = true;
}

// ── Stops ──
const stopRows = computed(() =>
  props.locations.map((l, i) => {
    const d = l.details;
    const line = [d.contact_name, formatPhone(d.contact_phone), d.reference].map((s) => (s || "").trim()).filter(Boolean);
    return {
      uid: l.uid,
      index: i,
      title: `${stopLabel(i, props.locations.length)} · ${shortPlaceName(l.name)}`,
      filled: hasStopDetails(d),
      line: line.join(" · "),
      instructions: (d.instructions || "").trim(),
    };
  }),
);

// One stop's editor open at a time; closing it puts focus back on its link.
const openStop = ref<string | null>(null);
const stopToggles = new Map<string, HTMLElement>();
function setStopToggle(uid: string, el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLElement) stopToggles.set(uid, el);
  else stopToggles.delete(uid);
}
function toggleStop(uid: string) {
  openStop.value = openStop.value === uid ? null : uid;
}
function closeStop(uid: string) {
  if (openStop.value !== uid) return;
  openStop.value = null;
  void nextTick(() => stopToggles.get(uid)?.focus());
}

const dropsWithoutContact = computed(() =>
  props.locations.slice(1).filter((l) => !(l.details.contact_phone || "").trim()).length,
);

// ── Optional extras ──
// Each is open when it has a value (so going back and forth shows it), or
// once the customer adds it. Removing one resets its value.
type ExtraKey = "loaders" | "driver" | "note";

const loadersShown = computed(() => !!props.loadersOffer && (props.loadersCount || 0) > 0);

const driverAdded = ref(!!(props.preferredDriver || "").trim());
const driverOpen = computed(() => driverAdded.value || !!(props.preferredDriver || "").trim());
const driverField = ref<{ focus: () => void } | null>(null);

const noteAdded = ref(false);
const noteOpen = computed(() => noteAdded.value || !!props.notes.trim() || !!props.manifest.trim());
const notesInput = ref<{ focus: () => void } | null>(null);
const notesModel = computed({ get: () => props.notes, set: (v: string) => emit("update:notes", v) });
const manifestModel = computed({ get: () => props.manifest, set: (v: string) => emit("update:manifest", v) });

const addChips = computed(() => {
  const chips: { key: ExtraKey; label: string }[] = [];
  if (props.loadersOffer && !loadersShown.value) chips.push({ key: "loaders", label: "Loaders" });
  if (props.preferredDriverEnabled && !driverOpen.value) chips.push({ key: "driver", label: "Preferred driver" });
  if (!noteOpen.value) chips.push({ key: "note", label: "Note" });
  return chips;
});

const addChipEls = new Map<ExtraKey, HTMLElement>();
function setAddChip(key: ExtraKey, el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLElement) addChipEls.set(key, el);
  else addChipEls.delete(key);
}

function openExtra(key: ExtraKey) {
  if (key === "loaders" && props.loadersOffer) {
    emit("update:loadersCount", Math.min(1, props.loadersOffer.max));
  } else if (key === "driver") {
    driverAdded.value = true;
    void nextTick(() => driverField.value?.focus());
  } else if (key === "note") {
    noteAdded.value = true;
    void nextTick(() => notesInput.value?.focus());
  }
}

// After a ×, focus goes to that extra's chip, back in the row.
function focusChip(key: ExtraKey) {
  void nextTick(() => addChipEls.get(key)?.focus());
}
function removeLoaders() {
  emit("update:loadersCount", 0);
  focusChip("loaders");
}
function removeDriver() {
  emit("update:preferredDriver", "");
  driverAdded.value = false;
  focusChip("driver");
}
function removeNote() {
  emit("update:notes", "");
  emit("update:manifest", "");
  noteAdded.value = false;
  focusChip("note");
}

// ── Confirm ──
const blocker = computed(() => {
  if (goodsError.value === "Add what you're sending") return "Add what you're sending";
  if (goodsError.value) return "Fix what you're sending";
  // Dedicated is always scheduled, whatever the mode says.
  const minLead = props.dedicated ? DEDICATED_MIN_LEAD_MINUTES : SCHEDULE_MIN_LEAD_MINUTES;
  if ((props.dedicated || props.pickupMode === "scheduled") && scheduleError(props.pickupTime, props.now, minLead)) {
    return "Choose a pickup time";
  }
  // A stop contact still open: Done saves (or shows what's wrong with it).
  if (openStop.value) return "Press Done on the stop contact first";
  return props.extraBlocker || "";
});

const confirmLabel = computed(() => `Confirm ${props.vehicleName} order · ${kes(props.total)}`);

function confirm() {
  goodsTouched.value = true;
  if (blocker.value) return;
  emit("confirm");
}
</script>

<template>
  <div class="ods">
    <div class="ods-head">
      <div class="ods-head__row">
        <button type="button" class="ods-icon-btn" aria-label="Back to prices" @click="emit('back')">
          <el-icon><ArrowLeft /></el-icon>
        </button>
        <div class="ods-head__title">{{ vehicleName }} · {{ kes(total) }}</div>
        <button type="button" class="ods-link-btn" @click="emit('edit-stops')">Edit stops</button>
      </div>
      <p class="ods-head__route">{{ routeSummary }}</p>
    </div>

    <div class="ods-body">
      <section class="ods-section">
        <span id="ods-stops-label" class="ods-label">Stops <span class="ods-optional">Contacts optional</span></span>
        <ol class="ods-stops" aria-labelledby="ods-stops-label">
          <li v-for="row in stopRows" :key="row.uid" class="ods-stop" :class="{ 'is-open': openStop === row.uid, 'is-last': row.index === stopRows.length - 1 }">
            <div class="ods-stop__row">
              <span class="ods-stop__num" :class="{ 'is-pickup': row.index === 0 }" aria-hidden="true">{{ row.index + 1 }}</span>
              <span class="ods-stop__main">
                <span class="ods-stop__title">{{ row.title }}</span>
                <button
                  :ref="(el) => setStopToggle(row.uid, el)"
                  type="button"
                  class="ods-stop__toggle"
                  :class="{ 'is-filled': row.filled }"
                  :aria-expanded="openStop === row.uid"
                  :aria-controls="`ods-stop-${row.uid}`"
                  :aria-label="row.filled ? `Edit contact for ${row.title}: ${row.line || row.instructions}` : `Add contact for ${row.title}`"
                  @click="toggleStop(row.uid)"
                >
                  <template v-if="row.filled">
                    <span v-if="row.line" class="ods-stop__line">{{ row.line }}</span>
                    <span v-else class="ods-stop__line ods-stop__line--note">“{{ row.instructions }}”</span>
                    <span class="ods-stop__edit" aria-hidden="true">Edit</span>
                  </template>
                  <template v-else>+ Add contact</template>
                </button>
              </span>
            </div>
            <div v-if="openStop === row.uid" :id="`ods-stop-${row.uid}`" class="ods-stop__editor">
              <StopDetailsEditor
                :id-base="`ods-stop-${row.uid}`"
                :title="row.title"
                :details="locations[row.index].details"
                @save="emit('save-stop', row.uid, $event)"
                @close="closeStop(row.uid)"
              />
            </div>
          </li>
        </ol>
      </section>

      <section class="ods-section">
        <label class="ods-label" for="ods-goods">What are you sending?<span class="ods-req" aria-hidden="true">*</span></label>
        <el-input
          id="ods-goods"
          :model-value="goods"
          :maxlength="GOODS_LIMITS.max"
          placeholder="e.g. 40 cartons of printer paper"
          autocomplete="off"
          aria-required="true"
          class="ods-input"
          :class="{ 'is-invalid': showGoodsError }"
          @update:model-value="emit('update:goods', $event)"
          @blur="goodsTouched = true"
        />
        <div class="ods-suggest" role="group" aria-label="Quick picks for what you're sending">
          <button
            v-for="g in GOODS_SUGGESTIONS"
            :key="g"
            type="button"
            class="ods-suggest__chip"
            :aria-pressed="goods.trim() === g"
            @click="pickGoods(g)"
          >{{ g }}</button>
        </div>
        <p v-if="showGoodsError" class="ods-error" role="status">{{ goodsError }}</p>
        <p v-else class="ods-help">Drivers see this before they accept, so leave out names and phone numbers.</p>
      </section>

      <section class="ods-section">
        <PickupTimeField
          :mode="pickupMode"
          :time="pickupTime"
          :now="now"
          :dedicated="dedicated"
          @update:mode="emit('update:pickupMode', $event)"
          @update:time="emit('update:pickupTime', $event)"
        />
        <p v-if="dedicated" class="ods-dedicated-note">Xobo assigns your driver before pickup and confirms with you.</p>
      </section>

      <!-- Optional extras: an "add" chip each; adding one opens it in place
           with a × that removes it again (and resets its value). -->
      <section class="ods-section ods-extras" aria-label="Optional extras">
        <div v-if="loadersShown" class="ods-extra">
          <div class="ods-extra__head">
            <span class="ods-extra__title">Loaders</span>
            <button type="button" class="ods-extra__remove" aria-label="Remove loaders" @click="removeLoaders">
              <el-icon aria-hidden="true"><Close /></el-icon>
            </button>
          </div>
          <LoadersField
            :model-value="loadersCount ?? 0"
            :unit-fee="loadersOffer?.unitFee ?? 0"
            :max="loadersOffer?.max ?? 1"
            @update:model-value="emit('update:loadersCount', $event)"
          />
        </div>

        <div v-if="preferredDriverEnabled" v-show="driverOpen" class="ods-extra">
          <div class="ods-extra__head">
            <span class="ods-extra__title">Preferred driver</span>
            <button type="button" class="ods-extra__remove" aria-label="Remove preferred driver" @click="removeDriver">
              <el-icon aria-hidden="true"><Close /></el-icon>
            </button>
          </div>
          <PreferredDriverField
            ref="driverField"
            :model-value="preferredDriver ?? ''"
            :vehicle-type-id="vehicleTypeId ?? 0"
            :dedicated="dedicated"
            @update:model-value="emit('update:preferredDriver', $event)"
          />
        </div>

        <div v-if="noteOpen" class="ods-extra">
          <div class="ods-extra__head">
            <span class="ods-extra__title">Note</span>
            <button type="button" class="ods-extra__remove" aria-label="Remove note and manifest number" @click="removeNote">
              <el-icon aria-hidden="true"><Close /></el-icon>
            </button>
          </div>
          <label class="sr-only" for="ods-notes">Notes</label>
          <el-input
            id="ods-notes"
            ref="notesInput"
            v-model="notesModel"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 6 }"
            placeholder="Type your notes here"
            class="ods-input"
          />
          <label class="ods-label ods-extra__sub" for="ods-manifest">Manifest no. <span class="ods-optional">(optional)</span></label>
          <el-input
            id="ods-manifest"
            v-model="manifestModel"
            placeholder="Record the manifest number"
            autocomplete="off"
            class="ods-input"
          />
        </div>

        <div v-if="addChips.length" class="ods-add" role="group" aria-label="Add an extra">
          <button
            v-for="c in addChips"
            :key="c.key"
            :ref="(el) => setAddChip(c.key, el)"
            type="button"
            class="ods-add__chip"
            :aria-expanded="false"
            @click="openExtra(c.key)"
          >
            <el-icon aria-hidden="true"><Plus /></el-icon>{{ c.label }}
          </button>
        </div>
      </section>

    </div>

    <div class="ods-foot">
      <p class="nr-hold-note">
        You will not be charged now. Payment is taken only after Xobo Ops approves the driver's delivery documents.
      </p>
      <p v-if="dropsWithoutContact > 0" class="ods-nudge">
        {{ dropsWithoutContact }} drop-off{{ dropsWithoutContact === 1 ? " has" : "s have" }} no contact. The driver will call you instead.
      </p>
      <ActionButton
        :label="confirmLabel"
        loading-label="Placing your order…"
        done-label="Order placed"
        :state="placing ? 'loading' : (placed ? 'done' : 'idle')"
        :disabled="!!blocker"
        @click="confirm"
      />
      <p v-if="blocker && !placing && !placed" class="ods-reason" role="status">{{ blocker }}</p>
    </div>
  </div>
</template>

<style scoped src="./order-form.css"></style>
<style scoped>
.ods { font-family: 'DM Sans', sans-serif; }

/* Navy head, as the stops head it replaces. */
.ods-head {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  padding: 16px 18px 14px 10px;
  border-radius: 16px 16px 0 0;
  color: #ffffff;
}
.ods-head__row { display: flex; align-items: center; gap: 6px; }
.ods-head__title {
  flex: 1;
  min-width: 0;
  font-size: 0.98rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.ods-head__route {
  margin: 2px 0 0 46px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.72);
}
.ods-icon-btn {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
}
.ods-icon-btn:hover { background: rgba(255, 255, 255, 0.1); }
.ods-link-btn {
  min-height: 40px;
  padding: 0 4px;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.85);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
}
.ods-link-btn:hover { color: #ffffff; }
.ods-icon-btn:focus-visible, .ods-link-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 2px;
}

.ods-body {
  padding: 16px 16px 8px;
  background: #ffffff;
}
.ods-section { margin: 0 0 16px; }
/* A hairline between groups instead of boxes. */
.ods-section + .ods-section { padding-top: 16px; border-top: 1px solid #f1f5f9; }

/* Goods quick picks */
.ods-suggest { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.ods-suggest__chip {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.ods-suggest__chip:hover { border-color: #12284A; color: #12284A; }
.ods-suggest__chip[aria-pressed="true"] { border-color: #12284A; background: #12284A; color: #ffffff; }
.ods-suggest__chip:focus-visible { outline: 2px solid #2d43ff; outline-offset: 2px; }

/* Stops: a numbered route line, each with a quiet contact link that opens
   the editor in place. */
.ods-stops { list-style: none; margin: 0; padding: 0; }
.ods-stop { position: relative; padding-bottom: 12px; }
.ods-stop.is-last { padding-bottom: 0; }
/* The line joining one stop's dot to the next. */
.ods-stop:not(.is-last)::before {
  content: "";
  position: absolute;
  left: 9px;
  top: 24px;
  bottom: 2px;
  width: 2px;
  border-radius: 1px;
  background: #e2e8f0;
}
.ods-stop__row { display: flex; align-items: flex-start; gap: 12px; }
.ods-stop__num {
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
  border-radius: 50%;
  background: #f59e0b;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ods-stop__num.is-pickup { background: #22c55e; }
.ods-stop__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ods-stop__title { font-size: 0.84rem; font-weight: 600; line-height: 22px; color: #12284A; overflow-wrap: anywhere; }
.ods-stop__toggle {
  align-self: flex-start;
  max-width: 100%;
  min-height: 40px;
  margin: -4px 0 -4px -6px;
  padding: 4px 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  background: none;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e3f73;
  text-align: left;
  cursor: pointer;
}
.ods-stop__toggle:hover { background: #f1f5f9; }
.ods-stop__toggle:focus-visible { outline: 2px solid #2d43ff; outline-offset: 0; }
.ods-stop__toggle.is-filled { font-weight: 500; color: #334155; }
.ods-stop__line { min-width: 0; overflow-wrap: anywhere; }
.ods-stop__line--note { font-style: italic; color: #64748b; }
.ods-stop__edit { flex-shrink: 0; font-weight: 600; color: #1e3f73; text-decoration: underline; text-underline-offset: 3px; }
.ods-stop__toggle[aria-expanded="true"] { background: #f1f5f9; }
.ods-stop__editor { margin-left: 32px; }

/* Optional extras */
.ods-extras { display: flex; flex-direction: column; gap: 12px; }
.ods-extra { padding: 12px; border-radius: 12px; background: #f8fafc; }
.ods-extra__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -8px -8px 4px 0;
}
.ods-extra__title { font-size: 0.8rem; font-weight: 600; color: #12284A; }
.ods-extra__remove {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
}
.ods-extra__remove:hover { background: #e2e8f0; color: #12284A; }
.ods-extra__remove:focus-visible { outline: 2px solid #2d43ff; outline-offset: 0; }
.ods-extra__sub { margin-top: 12px; }
.ods-add { display: flex; flex-wrap: wrap; gap: 8px; }
.ods-add__chip {
  min-height: 40px;
  padding: 0 14px 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px dashed #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #1e3f73;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.ods-add__chip:hover { border-color: #12284A; border-style: solid; background: #f8fafc; }
.ods-add__chip:focus-visible { outline: 2px solid #2d43ff; outline-offset: 2px; }
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* Footer: sticks to the bottom of the scrolling panel. */
.ods-foot {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 12px 16px 16px;
  background: #ffffff;
  border-top: 1px solid #eef2f7;
  box-shadow: 0 -8px 16px -12px rgba(18, 40, 74, 0.18);
  border-radius: 0 0 16px 16px;
}
/* Same copy as NewRequest's hold note, which it moved from; smaller. */
.nr-hold-note {
  margin: 0 0 8px;
  color: #6d5bb0;
  font-size: 0.7rem;
  line-height: 1.4;
  text-align: center;
}
.ods-nudge {
  margin: 0 0 8px;
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.4;
  text-align: center;
}
.ods-nudge::before {
  content: "";
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 6px 1px 0;
  border-radius: 50%;
  background: #f59e0b;
}
.ods-dedicated-note {
  margin: 8px 0 0;
  color: #475569;
  font-size: 0.74rem;
  line-height: 1.45;
}
.ods-reason {
  margin: 8px 0 0;
  font-size: 0.74rem;
  color: #64748b;
  text-align: center;
}
</style>
